import { FC, useEffect, useRef, useState } from 'react';
import { cloneDeep } from 'lodash';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { FormType } from "../../common";
import { DialogContainer } from "../../CommonDataParts/DialogContainer2";
import DsmButtonControlGroup from "../../../helpers/DsmButtonControlGroup";
import DairyV2HousingAndManureForm from './DairyV2HousingAndManureForm';
import {
  DairyV2Intervention,
  DairyV2DairyCowStageData,
  DairyV2ContinuousGrowingStageData,
  DairyV2Stage,
} from '../../../../models/Intervention/DairyV2Intervention';
import { CattleHousingAndManure } from '../../../../models/Intervention/CattleIntervention';
import { DairyV2Baseline } from '../../../../models/Baseline/DairyV2Baseline';
import { CattleBeddingSystem, CattleManureManagementSystem } from '../../../../models/Baseline/CattleBaseline';

export interface DairyV2HousingAndManureDialogProps {
  formType: FormType;
  stageIndex: number;
  formVisible: boolean;
  handleCancel: (param?: string | null) => void;
  baseline: DairyV2Baseline;
  stageType: string;
}

const DairyV2HousingAndManureDialog: FC<DairyV2HousingAndManureDialogProps> = ({
  formVisible,
  baseline,
  stageIndex = 0,
  formType = FormType.Add,
  handleCancel,
  stageType,
}) => {
  const intl = useIntl();

  const formContext = useFormContext<DairyV2Intervention>(); // create dairyV2 interventions
  const fieldItemPrefix = `stages.${stageIndex}.stageData.housing`;

  const formTitle = intl.formatMessage(
    { id: "SUSTELL.PROCESS.DIALOG.STAGE.MANURE.TITLE" },
    { br: " " }
  );

  const dairyStageData = baseline.stages[stageIndex].stageData as
    | DairyV2ContinuousGrowingStageData
    | DairyV2DairyCowStageData;

  const [isBeddingEdit, setIsBeddingEdit] = useState<boolean>(false);
  const [isTimeSpentEdit, setIsTimeSpentEdit] = useState<boolean>(false);

  const timeInHousing = formContext.watch(`${fieldItemPrefix}.timeInHousing`);

  const currResetValue = useRef<CattleHousingAndManure>();

  useEffect(() => {
    currResetValue.current = cloneDeep(formContext.getValues(fieldItemPrefix));
  }, []);

  const {
    fields: beddingSystemsAdditions,
    append: appendBeddingSystems,
    remove: removeBeddingSystems,
  } = useFieldArray({
    name: `${fieldItemPrefix}.beddingSystemsAdditions`,
    control: formContext.control,
    keyName: "keyId",
  });

  const {
    fields: mmsAdditions,
    append: appendMMSAdditions,
    remove: removeMMSAdditions,
  } = useFieldArray({
    name: `${fieldItemPrefix}.manureSystemsAdditions`,
    control: formContext.control,
    keyName: "keyId",
  });

  const beddingCancelHandler = (isInnerCancel = true) => {
    if ((currResetValue.current?.beddingSystems?.length ?? 0) === 0) {
      (dairyStageData.housing.beddingSystems || []).forEach((bedding, index) => {
        const fieldPrefix = `${fieldItemPrefix}.beddingSystems[${index}]`;
        Object.keys(bedding).forEach((key) => {
          formContext.setValue(`${fieldPrefix}.${key}`, "")
        })
      })
    }

    const beddingAdditions = formContext.getValues(`${fieldItemPrefix}.beddingSystemsAdditions`) as CattleBeddingSystem[];
    if ((beddingAdditions?.length ?? 0) > 0) {
      for (let i = beddingAdditions.length; i >= 0; i--) {
        removeBeddingSystems(i);
      }
    }

    if ((currResetValue.current?.beddingSystemsAdditions?.length ?? 0) > 0) {
      currResetValue.current?.beddingSystemsAdditions?.forEach((bedding) => {
        appendBeddingSystems(bedding);
      })
    }

    if (isInnerCancel) {
      formContext.setValue(`${fieldItemPrefix}.beddingSystems`, currResetValue.current?.beddingSystems);
      formContext.setValue(`${fieldItemPrefix}.beddingSystemsAdditions`, currResetValue.current?.beddingSystemsAdditions);
    }

    setIsBeddingEdit(false);
  }

  const totalTimeSpentReset = (isInnerCancel: boolean) => {
    if (currResetValue.current?.timeInHousing === undefined)
      formContext.setValue(`${fieldItemPrefix}.timeInHousing`, "");

    if (currResetValue.current?.timeInFeedlot === undefined)
      formContext.setValue(`${fieldItemPrefix}.timeInFeedlot`, "");

    if (currResetValue.current?.timeGrazingLargeAreas === undefined)
      formContext.setValue(`${fieldItemPrefix}.timeGrazingLargeAreas`, "");

    if (currResetValue.current?.timeGrazingPastures === undefined)
      formContext.setValue(`${fieldItemPrefix}.timeGrazingPastures`, "");

    if (isInnerCancel) {
      formContext.setValue(`${fieldItemPrefix}.timeInHousing`, currResetValue.current?.timeInHousing);
      formContext.setValue(`${fieldItemPrefix}.timeInFeedlot`, currResetValue.current?.timeInFeedlot);
      formContext.setValue(`${fieldItemPrefix}.timeGrazingLargeAreas`, currResetValue.current?.timeGrazingLargeAreas);
      formContext.setValue(`${fieldItemPrefix}.timeGrazingPastures`, currResetValue.current?.timeGrazingPastures);
    }
  }

  const mmsReset = (isInnerCancel: boolean) => {
    if (currResetValue.current?.timeInHousingTied === "")
      formContext.setValue(`${fieldItemPrefix}.timeInHousingTied`, "");

    if ((currResetValue.current?.manureSystems?.length ?? 0) === 0) {
      (dairyStageData.housing.manureSystems || []).forEach((mms, index) => {
        const fieldPrefix = `${fieldItemPrefix}.manureSystems.${index}`;
        Object.keys(mms).forEach((key => {
          formContext.setValue(`${fieldPrefix}.${key}`, "");
        }))
      })
    }

    const mmsItemAdditions = formContext.getValues(`${fieldItemPrefix}.manureSystemsAdditions`) as CattleManureManagementSystem[];
    if ((mmsItemAdditions?.length ?? 0) > 0) {
      for (let i = mmsItemAdditions.length; i >= 0; i--) {
        removeMMSAdditions(i);
      }
    }

    if ((currResetValue.current?.manureSystemsAdditions?.length ?? 0) > 0) {
      currResetValue.current?.manureSystemsAdditions?.forEach((mms) => {
        appendMMSAdditions(mms);
      })
    }

    if (isInnerCancel) {
      formContext.setValue(`${fieldItemPrefix}.manureSystems`, currResetValue.current?.manureSystems);
      formContext.setValue(`${fieldItemPrefix}.manureSystemsAdditions`, currResetValue.current?.manureSystemsAdditions);
    }
  }

  const timeSpentCancelHandler = (isInnerCancel = true) => {
    totalTimeSpentReset(isInnerCancel);
    mmsReset(isInnerCancel);

    setIsTimeSpentEdit(false);
  }

  const handleResetClick = () => {
    beddingCancelHandler(false);
    timeSpentCancelHandler(false);

    formContext.setValue(`${fieldItemPrefix}`, currResetValue.current);

    if (handleCancel)
      handleCancel();
  };

  const shouldDisplayMMSRows = () => {
    const currentHousingTime = parseFloat(timeInHousing as any);
    const initialHousingTime = parseFloat(dairyStageData.housing.timeInHousing as any);

    return currentHousingTime === 0 || (isNaN(currentHousingTime) && initialHousingTime === 0);
  };

  useEffect(() => {
    if (shouldDisplayMMSRows()) {
      formContext.setValue(`${fieldItemPrefix}.manureSystems`, []);

      if (formContext.getValues(`${fieldItemPrefix}.timeInHousingTied`) !== undefined)
        formContext.setValue(`${fieldItemPrefix}.timeInHousingTied`, 0);

      for (let index = mmsAdditions.length; index >= 0; index--) {
        removeMMSAdditions(index);
      }
    }
  }, [timeInHousing]);

  return (
    <DialogContainer
      formVisible={formVisible}
      variant="wide"
      handleClose={handleResetClick}
      iconCode="general/building-06"
      formTitle={formTitle}
      datasetType="intervention"
    >
      <DairyV2HousingAndManureForm
        formContext={formContext}
        formType={formType}
        stageIndex={stageIndex}
        baseline={baseline.stages[stageIndex] as DairyV2Stage}
        stageType={stageType}
        fieldItemPrefix={fieldItemPrefix}
        dairyStageData={dairyStageData}
        beddingSystemsCollection={{
          isBeddingEdit: isBeddingEdit,
          setIsBeddingEdit: setIsBeddingEdit,
          beddingSystemsAdditions: beddingSystemsAdditions,
          appendBeddingSystems: appendBeddingSystems,
          removeBeddingSystems: removeBeddingSystems,
          beddingCancelHandler: beddingCancelHandler,
        }}
        mmsCollection={{
          mmsAdditions: mmsAdditions,
          appendMMSAdditions: appendMMSAdditions,
          removeMMSAdditions: removeMMSAdditions,
        }}
        timeSpentCollection={{
          isTimeSpentEdit: isTimeSpentEdit,
          setIsTimeSpentEdit: setIsTimeSpentEdit,
          timeSpentCancelHandler: timeSpentCancelHandler,
        }}
      />
      <DsmButtonControlGroup
        cancelHandler={handleResetClick}
        saveHandler={async () => {
          await formContext.trigger(`stages.${stageIndex}.stageData.housing`);
          handleCancel("confirm");
        }}
        saveLabel={intl.formatMessage({ id: "GENERAL.CONFIRM" })}
      />
    </DialogContainer>
  );
};

export default DairyV2HousingAndManureDialog;
